import { Close, Download, Upload } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import { CalendarIcon, DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import _, { indexOf } from "lodash";
import { Moment } from "moment-timezone";
import { parse, ParseResult } from "papaparse";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCountry } from "src/components/Context/CountryContext";
import PageContext from "src/components/Context/PageContext";
import { useTitle } from "src/hooks/useTitle";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { WasPriceOverrideResponse } from "src/models/product/WasPriceOverrideResponse";
import { formatCurrency } from "src/utils/formatUtil";
import { sendWasPricesAsync } from "../../infrastructure/http/appApiService";
import AddSkuDialog from "./Dialogs/AddSkuDialog";
import ResetConfirmationDialog from "./Dialogs/ResetConfirmationDialog";
import { WasPriceConfirmationDialog } from "./Dialogs/WasPriceConfirmationDialog";
import { useTooltipMessages } from "./Hooks/useToolTipMessages";
import TooltipWrapper from "./TooltipWrapper";
import { WasPriceUsage } from "./WasPriceUsage";
import { InvalidWasPriceConfirmationDialog } from "./Dialogs/InvalidWasPriceConfirmationDialog";

var moment = require("moment-timezone");
moment.tz.setDefault("Australia/Melbourne");
const localDateFormat = "DD/MM/YYYY HH:mm:ss";
const UTCFormat = "YYYY-MM-DDTHH:mm:ss.SSS[Z]";

const WasPriceOverrideTypes = {
  All: "All",
  Public: "Public",
  Perks: "Perks",
};

type WasPriceOverrideType = (typeof WasPriceOverrideTypes)[keyof typeof WasPriceOverrideTypes];

export interface WasPriceOverrideModel {
  Sku: string;
  WasPriceOverride: number;
  StartTime: string;
  EndTime: string;
  StartDate: string;
  EndDate: string;
  Error: string;
  Comment: string;
  AuthorisedBy: string;
  Type: WasPriceOverrideType;
}

export interface WasPriceOverride extends WasPriceOverrideModel {
  id: string;
  Valid: boolean;
  ValidPrices: number[];
}

const columns: GridColDef[] = [
  {
    field: "Sku",
    headerName: "Sku",
    width: 100,
    editable: false,
  },
  {
    field: "WasPriceOverride",
    headerName: "Was Price",
    type: "number",
    width: 100,
    editable: true,
  },
  {
    field: "StartTime",
    headerName: "Start Date",
    width: 170,
    disableExport: true,
    editable: false,
  },
  {
    field: "EndTime",
    headerName: "End Date",
    width: 170,
    disableExport: true,
    editable: false,
  },
  {
    field: "Type",
    headerName: "Type",
    width: 100,
    editable: false,
  },
  {
    field: "AuthorisedBy",
    headerName: "Authorised By",
    width: 150,
    editable: false,
  },
  {
    field: "Comment",
    headerName: "Comment",
    width: 200,
  },
  {
    field: "Valid",
    headerName: "Valid",
    width: 75,
    type: "boolean",
    disableExport: true,
    editable: false,
    renderCell: (value) => {
      return (
        <div>
          {value.value == true && <DoneIcon />}
          {value.value == false && <ClearIcon />}
        </div>
      );
    },
  },
  {
    field: "ValidPrices",
    headerName: "Available Prices",
    flex: 1,
    editable: false,
    renderCell: (value) => {
      return <div>{(value.value ?? []).map((v) => formatCurrency(v)).join(", ")}</div>;
    },
    valueFormatter: (value) => (value.value ?? []).map((v) => formatCurrency(v)).join(" "),
  },
];

const WasPricesComponent = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { country, timezone } = useCountry();
  const momentInstance = moment.tz.setDefault(timezone);
  const [endDate, setEndDate] = useState<Moment | null>(moment().set("hour", 23).set("minute", 59).set("second", 0));
  const [startDate, setStartDate] = useState<Moment | null>(moment());
  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;

  useTitle("Was Prices");

  //Single SKU Overrides
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [startDateOpen, setStartDateOpen] = useState<boolean>(false);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [invalidWasPriceDialogOpen, setInvalidWasPriceDialogOpen] = useState<boolean>(false);

  function CustomToolbar() {
    return <GridToolbarContainer>{endDate && comment && generated && <GridToolbarExport />}</GridToolbarContainer>;
  }

  //State to store the values
  const [values, setValues] = useState<WasPriceOverride[]>([]);
  const [apiPostValues, setApiPostValues] = useState(values);

  useEffect(() => {
    const newApiPostValues = values.flatMap((o) => {
      // The request payload needs to be updated for both Public and Perks when the type is ALL
      if (o.Type === WasPriceOverrideTypes.All) {
        const publicWasPriceOverride = Object.assign({}, o, { Type: WasPriceOverrideTypes.Public });
        const perksWasPriceOverride = Object.assign({}, o, { Type: WasPriceOverrideTypes.Perks });

        return [publicWasPriceOverride, perksWasPriceOverride];
      }

      return [o];
    });

    setApiPostValues(newApiPostValues);
  }, [values]);

  const handleCellEditCommit = useCallback(
    ({ id, field, value }) => {
      if (field === "WasPriceOverride") {
        const updatedRows = values.map((row) => {
          if (row.id === id) {
            return { ...row, WasPriceOverride: value };
          }
          return row;
        });
        setValues(updatedRows);
      }
    },
    [values]
  );

  const [errors, setErrors] = useState<string[]>([]);
  const [headerError, setHeaderError] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [sku, setSku] = useState<string>("");
  const [wasPriceOverride, setWasPriceOverride] = useState<number | null>(null);
  const [comment, setComment] = useState<string>("");
  const [type, setType] = useState<WasPriceOverrideType>(WasPriceOverrideTypes.All);
  const [generated, setGenerated] = useState<boolean>();
  const [postingToApi, setPostingToApi] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const verifyValues = (wasPrices: WasPriceOverride[]) => {
    let csvErrors = [];

    wasPrices.forEach((item, index) => {
      if (!item?.Sku.trim() || item.WasPriceOverride === undefined || isNaN(item.WasPriceOverride)) {
        csvErrors.push(`Row ${index + 1}: Empty Sku or invalid WasPriceOverride`);
      } else if (item.WasPriceOverride < 0) {
        csvErrors.push(`Row ${index + 1}: WasPriceOverride cannot be negative`);
      }

      // Check if Sku contains only alphanumeric characters
      if (!/^[a-zA-Z0-9]+$/.test(item.Sku)) {
        csvErrors.push(`Row ${index + 1}: Sku contains invalid characters`);
      }
    });

    if (csvErrors.length) {
      setErrors(csvErrors);
    } else {
      setErrors([]);
    }
  };

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderError("");
    setGenerated(false);
    const expectedHeaders = ["Sku", "WasPriceOverride"];

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    parse(file, {
      header: true,
      skipEmptyLines: true,
      beforeFirstChunk: function (chunk) {
        const rows = chunk.split(/\r?\n|\r|\n/g);
        if (rows.length > 0) {
          const headers = rows[0].split(",").map((h) => h.trim());
          if (!_.isEqual(headers, expectedHeaders)) {
            setHeaderError("CSV headers do not match the provided template.");
          }
        }
      },
      complete: function (results: ParseResult<Record<any, any>>) {
        const valuesArray: WasPriceOverride[] = (results.data as WasPriceOverride[]).filter(
          (x) => x.Sku.trim() != "" && x.WasPriceOverride != undefined && x.Sku.trim() != ""
        );

        for (let i = 0; i < valuesArray.length; i++) {
          valuesArray[i].WasPriceOverride = parseFloat(valuesArray[i].WasPriceOverride as unknown as string);
          valuesArray[i].Sku = valuesArray[i].Sku.trim();
          valuesArray[i].id = crypto.randomUUID();
        }
        verifyValues(valuesArray);
        setValues(valuesArray);
      },
    });
    event.target.value = "";
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const generateDocument = () => {
    const utcStartDate = startDate.tz(timezone).utc().format(UTCFormat);
    let utcEndDate;

    if (endDate) {
      const endDateTime = moment(endDate);
      utcEndDate = endDateTime.utc().format(UTCFormat);
    }

    if (!utcEndDate) {
      console.error("End Date and Time are not properly set");
      return;
    }

    const localEndDate = moment.utc(utcEndDate).tz(timezone).format(localDateFormat);
    const localStartDate = moment.utc(utcStartDate).tz(timezone).format(localDateFormat);

    values.forEach(function (item: WasPriceOverride, index: number) {
      item.StartTime = localStartDate;
      item.EndTime = localEndDate;
      item.EndDate = utcEndDate;
      item.AuthorisedBy = userProfile.displayName;
      item.Comment = comment!;
      item.StartDate = utcStartDate;
      item.Type = type;
    });
    setValues([...values]);
    setGenerated(true);
    resetDetailFormValues();
  };

  const resetValues = () => {
    setComment("");
    setType(WasPriceOverrideTypes.All);
    setValues([]);
    setGenerated(false);
    setEndDate(moment().set("hour", 23).set("minute", 59).set("second", 0));
    setStartDate(moment());
    setHeaderError("");
  };

  const resetFormValues = () => {
    setComment("");
    setType(WasPriceOverrideTypes.All);
    setValues([]);
    setGenerated(false);
    setSku("");
    setWasPriceOverride(null);
    setHeaderError("");
  };

  const resetDetailFormValues = () => {
    setComment("");
    setType(WasPriceOverrideTypes.All);
    setSku("");
    setWasPriceOverride(null);
    setHeaderError("");
  };

  const resetStartDate = () => {
    setStartDate(null);
  };

  const postToPricingApi = async () => {
    setPostingToApi(true);
    try {
      if (values.some((y) => y.WasPriceOverride == null || y.WasPriceOverride < 0)) {
        let items = values;
        items.forEach((x) => (x.Valid = x.WasPriceOverride != null && x.WasPriceOverride >= 0));
        setValues(items);
        setSnackbar({
          open: true,
          message: "One or more rows do not have a valid was price",
          severity: "error",
        });
        return;
      }
      var result = await sendWasPricesAsync(
        apiPostValues.map((x) => {
          return {
            Sku: x.Sku,
            WasPriceOverride: x.WasPriceOverride,
            StartTime: x.StartTime,
            EndTime: x.EndTime,
            StartDate: x.StartDate,
            EndDate: x.EndDate,
            Error: x.Error,
            Comment: x.Comment,
            AuthorisedBy: x.AuthorisedBy,
            Type: x.Type,
          } as WasPriceOverrideModel;
        }) as WasPriceOverrideModel[],
        country
      );

      if (result.success) {
        setSnackbar({
          open: true,
          message: "Was prices override completed successfully",
          severity: "success",
        });
        resetValues();
      } else {
        setSnackbar({
          open: true,
          message: result.errorMessage,
          severity: "error",
        });
      }
    } catch (error) {
      let errorMessage = "Error occurred while overriding was prices.";
      if (error.response && typeof error.response.data === "string") {
        errorMessage = error.response.data;
      } else if (error.response && error.response.data) {
        let responseAsModel = error.response.data as WasPriceOverrideResponse;
        if (responseAsModel) {
          errorMessage = error.response.data.errorMessage;
          var items = values;
          items.forEach((x) => {
            var invalidItem = responseAsModel.errors.find(
              (error) => error.sku == x.Sku && (error.type == x.Type || x.Type == "All")
            );
            x.Valid = invalidItem == undefined;
            x.ValidPrices = invalidItem?.prices ?? ([] as number[]);
          });
          setValues(items);
          setInvalidWasPriceDialogOpen(true);
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.request) {
        errorMessage = "No response was received from the server.";
      } else {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setPostingToApi(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "comment") {
      setComment(value);
      setGenerated(false);
    }
  };

  const [usage, setUsage] = useState<boolean>(() => {
    const savedUsage = localStorage.getItem("showWasPriceUsage");
    if (savedUsage !== null) {
      return JSON.parse(savedUsage);
    }
    return true;
  });

  const handleClick = () => {
    const newUsage = !usage;
    setUsage(newUsage);
    localStorage.setItem("showWasPriceUsage", JSON.stringify(newUsage));
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInvalidWasPriceOpenDialog = () => {
    setInvalidWasPriceDialogOpen(true);
  };

  const handleInvalidWasPriceCloseDialog = () => {
    setInvalidWasPriceDialogOpen(false);
  };

  const handleAddSku = () => {
    if (wasPriceOverride !== null) {
      const newValue = {
        id: crypto.randomUUID(),
        Sku: sku,
        WasPriceOverride: wasPriceOverride,
      } as WasPriceOverride;
      setValues([...values, newValue]);
      setSku(null);
      setWasPriceOverride(null);
    } else {
      console.error("Was Price is not set or invalid.");
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (generated) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [generated]);

  const { noSkusTooltip, generateTooltip } = useTooltipMessages({
    values,
    endDate,
    startDate,
    comment,
    errors,
    generated,
  });

  if (!hasPermission(UserRoles.PricingWasPrices, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={momentInstance}>
      <Box sx={{ maxWidth: "1400px", margin: "0 auto", boxSizing: "border-box" }}>
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          spacing={2}
          alignContent={"center"}
          justifyContent={"space-between"}
        >
          <h1
            style={{
              display: "flex",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            Was Price Override Generator
          </h1>
          <Stack direction={"column"} spacing={1} alignItems="right">
            <Button
              size="medium"
              variant="outlined"
              component="label"
              disabled={values.length !== 0}
              startIcon={<Upload />}
            >
              Bulk Upload
              <input type="file" ref={fileInputRef} name="file" onChange={changeHandler} accept=".csv" hidden />
            </Button>
            <Button
              variant="text"
              size="small"
              component={Link}
              href="/was-price-template.csv"
              download="was-price-template.csv"
              sx={{
                fontSize: "9pt",
                color: "gray",
                textDecoration: "underline",
              }}
            >
              Download bulk upload template
            </Button>
          </Stack>
        </Stack>
        <AddSkuDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onConfirm={handleAddSku} />
        <ResetConfirmationDialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          onConfirm={resetValues}
        />
        <Box border={1} borderColor="lightgrey" width={"100%"} marginBottom={3} paddingBottom={7}>
          <Grid container spacing={1} justifyContent="center" alignItems="center" pt={6} rowGap={3}>
            {headerError && (
              <Grid xs={12}>
                <Alert severity="error">{headerError}</Alert>
              </Grid>
            )}
            <Grid xs={10} justifyContent="center" alignItems="center">
              <h1
                style={{
                  display: "flex",
                  justifyContent: "left",
                  fontWeight: "bold",
                }}
              >
                SKU and pricing details
              </h1>
            </Grid>
            <Grid xs={12} container spacing={2} justifyContent="center" alignItems="center">
              <Grid xs={4}>
                <TextField
                  autoFocus
                  margin="dense"
                  label="SKU"
                  type="text"
                  fullWidth
                  disabled={generated}
                  value={sku !== null ? sku : ""}
                  onChange={(e) => setSku(e.target.value.trim())}
                />
              </Grid>
              <Grid xs={4}>
                <TextField
                  margin="dense"
                  label="Was Price Override"
                  type="number"
                  fullWidth
                  disabled={generated}
                  value={wasPriceOverride !== null ? wasPriceOverride : ""}
                  InputProps={{ inputProps: { min: 0 } }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setWasPriceOverride(value === "" ? null : parseFloat(value));
                  }}
                />
              </Grid>

              <Grid xs={2}>
                <Button
                  variant="contained"
                  disabled={sku === "" || wasPriceOverride === null || wasPriceOverride < 0 || generated}
                  onClick={handleAddSku}
                  fullWidth
                >
                  Add
                </Button>
              </Grid>
            </Grid>
            <Grid xs={10} justifyContent="center" alignItems="center">
              <h1
                style={{
                  display: "flex",
                  justifyContent: "left",
                  fontWeight: "bold",
                }}
              >
                Override details
              </h1>
              <p style={{ paddingTop: "5px", color: "gray" }}>These details will apply to all SKUs added above</p>
            </Grid>
            <Grid xs={12} container justifyContent="center" alignItems="center" rowSpacing={3} columnSpacing={2}>
              <Grid xs={4}>
                <DateTimePicker
                  open={startDateOpen}
                  onClose={() => setStartDateOpen(false)} // This is called when the picker requests to be closed
                  onAccept={() => setStartDateOpen(false)}
                  sx={{ width: "100%" }}
                  label="Start Time"
                  value={startDate}
                  disabled={values.length === 0 || generated}
                  onChange={(newValue: Moment | null) => {
                    const errorMsg = "Invalid date and time format";

                    if (newValue && newValue.isValid()) {
                      setStartDate(newValue);
                      setGenerated(false);
                      setErrors((prevErrors) => prevErrors.filter((err) => err !== errorMsg));
                    } else {
                      if (!errors.includes(errorMsg)) {
                        setErrors((prevErrors) => [...prevErrors, errorMsg]);
                      }
                    }
                  }}
                  format="DD/MM/YYYY HH:mm"
                  slotProps={{
                    textField: {
                      InputProps: {
                        endAdornment: (
                          <>
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  setStartDateOpen(true);
                                }}
                                edge="end"
                              >
                                <CalendarIcon />
                              </IconButton>
                            </InputAdornment>
                            <InputAdornment position="end">
                              <IconButton onClick={resetStartDate} edge="end">
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          </>
                        ),
                      },
                    },
                  }}
                />
              </Grid>
              <Grid xs={4}>
                <DateTimePicker
                  sx={{ width: "100%" }}
                  disabled={values.length === 0 || generated}
                  label="End Date"
                  value={endDate}
                  onChange={(newValue: Moment | null) => {
                    const genericErrorMsg = "End date is invalid";
                    const futureErrorMsg = "End date cannot be before the current date";
                    const minValidDate = moment();
                    let newErrors = [...errors];
                    if (newValue && newValue.isValid()) {
                      newErrors = newErrors.filter((err) => err !== genericErrorMsg);
                    } else {
                      if (!newErrors.includes(genericErrorMsg)) {
                        newErrors.push(genericErrorMsg);
                      }
                    }
                    if (newValue && newValue.isSameOrAfter(minValidDate, "day")) {
                      newErrors = newErrors.filter((err) => err !== futureErrorMsg);
                    } else {
                      if (!newErrors.includes(futureErrorMsg)) {
                        newErrors.push(futureErrorMsg);
                      }
                    }
                    setErrors(newErrors);
                    setGenerated(false);
                    if (newErrors.length === 0) {
                      setEndDate(newValue);
                    }
                  }}
                  format="DD/MM/YYYY HH:mm"
                  minDate={moment()}
                />
              </Grid>
              <Grid xs={2}></Grid>
              <Grid xs={4}>
                <TextField
                  required
                  fullWidth
                  name="comment"
                  value={comment}
                  disabled={values.length === 0 || generated}
                  label="Comment"
                  onChange={handleChange}
                />
              </Grid>
              <Grid xs={4}>
                <FormControl fullWidth variant="outlined" disabled={values.length === 0}>
                  <InputLabel id="type-label">Type</InputLabel>
                  <Select
                    required
                    disabled={values.length === 0 || generated}
                    labelId="type-label"
                    value={type}
                    onChange={(event) => {
                      setType(event.target.value as WasPriceOverrideType);
                      setGenerated(false);
                    }}
                    label="Type"
                  >
                    {Object.values(WasPriceOverrideTypes).map((WasPriceOverrideType) => {
                      return (
                        <MenuItem key={WasPriceOverrideType} value={WasPriceOverrideType}>
                          {WasPriceOverrideType}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={2}></Grid>
            </Grid>
          </Grid>
        </Box>
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="text" onClick={() => setResetDialogOpen(true)}>
            Reset
          </Button>
          <TooltipWrapper title={generateTooltip}>
            <LoadingButton
              loading={postingToApi}
              variant="contained"
              disabled={
                (!generated && (endDate == null || !comment || !type || startDate == null)) || errors.length > 0
              }
              onClick={!generated ? generateDocument : () => setOpenDialog(true)}
              sx={{
                minWidth: "200px",
              }}
            >
              {!generated ? "Review" : `Submit for ${type} ${country}`}
            </LoadingButton>
          </TooltipWrapper>
        </Box>
        {/* Table */}
        <Box sx={{ height: "800px", width: "100%", mt: 4 }}>
          <DataGrid
            density="compact"
            components={{ Toolbar: CustomToolbar }}
            componentsProps={{
              toolbar: {
                csvOptions: {
                  fields: [
                    "Sku",
                    "StartDate",
                    "EndDate",
                    "WasPriceOverride",
                    "AuthorisedBy",
                    "Comment",
                    "Valid",
                    "ValidPrices",
                  ],
                },
              },
            }}
            rows={values}
            sx={{ padding: "0px 32px", maxWidth: "1400px", margin: "auto" }}
            columns={columns}
            pageSize={100}
            rowsPerPageOptions={[100]}
            disableSelectionOnClick
            experimentalFeatures={{ newEditingApi: false }}
            onCellEditCommit={handleCellEditCommit}
          />
        </Box>
        <WasPriceConfirmationDialog
          perks={type === "Perks"}
          loading={postingToApi}
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onConfirm={async () => {
            setPostingToApi(true);
            await postToPricingApi();
            setPostingToApi(false);
            setOpenDialog(false);
          }}
        />
        <InvalidWasPriceConfirmationDialog
          loading={postingToApi}
          open={invalidWasPriceDialogOpen}
          onClose={() => setInvalidWasPriceDialogOpen(false)}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prevState) => ({ ...prevState, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar((prevState) => ({ ...prevState, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default WasPricesComponent;
